import { createSelector } from '@ngrx/store';
import { GlobalState } from './index';
import { locationDataAdapter } from './core-adapters';
import { flatten, uniqBy } from 'lodash';
import { MainRoutes } from '../../core/enums';
import { ManagementStatusListEnum } from '../../components/locations-skeleton/enums/management-status-list-enum';
import { BusinessStatusListEnum } from '../../components/locations-skeleton/enums/business-status-list-enum';
import { DropdownOption } from '../../models/dropdown-option';
import { isValidCountryISOCode } from '../../core/utilities';
import { UserRole } from '../../components/users/enums/user-role-enum';
import { AiInsightsMode } from '../../models/account';

export const selectCoreState = (state: GlobalState) => state.core;
export const selectManagedLocationCount = createSelector(selectCoreState, (core) => core.managedLocationCount);
export const selectLastManagedLocationCountRefreshTimestamp = createSelector(selectCoreState, (core) => core.lastManagedLocationCountRefreshTimestamp);
export const selectGoogleMapsApiLoaded = createSelector(selectCoreState, (core) => core.googleMapsApiLoaded);
// user selectors
export const selectUser = createSelector(selectCoreState, (core) => core.user);
export const selectUserDetails = createSelector(selectCoreState, (core) => core.userDetails);
export const selectUserLanguage = createSelector(selectCoreState, (core) => core.userDetails?.language);
export const selectUserStatus = createSelector(selectUserDetails, (userDetails) => userDetails?.userStatus);
export const selectUserRole = createSelector(selectUserDetails, (userDetails) => userDetails?.role);
export const selectUserFeatures = createSelector(selectUserDetails, (userDetails) => userDetails?.userFeatures);
export const selectUserAiReplies = createSelector(selectUserFeatures, (features) => features?.aiRepliesActive);
export const selectUserSocialMediaActive = createSelector(selectUserFeatures, (features) => features?.socialMediaActive);
export const selectUserPermissions = createSelector(selectUserDetails, (userDetails) => userDetails?.permissions);
export const selectUserGxWrapped = createSelector(selectUserDetails, (userDetails) => userDetails?.gxWrapped);
export const selectJasonerEnabled = createSelector(selectUserDetails, (userDetails) => userDetails?.theJasonerEnabled);
// Below will be populated only for users and userAdmins, in case of LOC MNGR and up, there will be empty arrays
export const selectAccessibleGmbLocationIds = createSelector(selectUserDetails, (userDetails) => userDetails?.accessibleGmbLocationIds);
export const selectAccessibleGmbAccountIds = createSelector(selectUserDetails, (userDetails) => userDetails?.accessibleGmbAccountIds);
// Don't show business grp filters in user-facing components if they don't have access to any
export const selectBusinessGroupsFiltersHidden = createSelector(selectUserRole, selectAccessibleGmbAccountIds, (role, accIds) => {
  return role === UserRole.USER || role === UserRole.USER_ADMIN && !accIds?.length;
});
// account selectors
export const selectAccount = createSelector(selectCoreState, (core) => core.selectedAccount);
export const selectAccounts = createSelector(selectCoreState, (core) => core.accounts);
export const selectAccountID = createSelector(selectCoreState, (core) => core.selectedAccount?.id);
export const selectAccountName = createSelector(selectCoreState, (core) => core.selectedAccount?.accountName);
export const selectGMClientApiKey = createSelector(selectCoreState, (core) => core.selectedAccount?.gmclientApiKey);
export const selectGdprConsent = createSelector(selectCoreState, (core) => core.gdprConsent);
export const selectAccountAiInsightsMode = (createSelector(selectAccount, (account) => account?.aiInsightsMode));
export const selectAccountAiInsightsEnabled = (createSelector(selectAccountAiInsightsMode, (mode) => mode === AiInsightsMode.ENABLED));
export const selectAccountAiReplies = (createSelector(selectAccount, (account) => account?.aiReplies));
export const selectAccountCampaignsActive = (createSelector(selectAccount, (account) => account?.campaignsActivated));
// In order to enable AI replies features, AI replies need to be enabled both on the account and user level!!!
export const selectAiRepliesActivated = createSelector(selectAccountAiReplies,
    selectUserAiReplies,
    (accountAiReplies, userAiReplies) => accountAiReplies && userAiReplies);

// location (listings) data selectors
export const selectLocationDataState = createSelector(selectCoreState, (core) => core.locationData);

export const { selectAll: selectLocationDataList } =
    locationDataAdapter.getSelectors(selectLocationDataState);
export const selectLocationDataLoading = createSelector(selectCoreState, (core) => core.locationDataLoading);

// filter location data for campaigns BLS-7789
export const selectFilteredLocationDataList = (route: MainRoutes) => createSelector(selectLocationDataList, (locationDataList) => {
  if (route === MainRoutes.POSTS) {
    return locationDataList.filter((locationData) => {
      return (locationData.managementStatus === ManagementStatusListEnum.AUTOMATICALLY_MANAGED ||
              locationData.managementStatus === ManagementStatusListEnum.MANUALLY_MANAGED) &&
          locationData.businessStatus === BusinessStatusListEnum.OPEN;
    });
  } else return locationDataList;
});

// Listing dropdown options
export const selectUniqueMyBAccounts = (route: MainRoutes) => createSelector(selectFilteredLocationDataList(route), (locationDataList) => {
  // map location grps than flatten the resulting array of arrays and filter unique location grps by 'gmbAccountId'
  return uniqBy(flatten(locationDataList.map(locationData => locationData.myBAccountDtos)), 'gmbAccountId');
});

export const selectLocationGroups = (route: MainRoutes) => createSelector(selectUniqueMyBAccounts(route), (accounts) => {
  // map to DropdownOptions
  return accounts.map(grp => ({ name: grp.name, value: grp.gmbAccountId }))
      // sort alphabetically
      .sort((a, b) => a.name.localeCompare(b.name));
});

export const selectCities = (route: MainRoutes) => createSelector(selectFilteredLocationDataList(route), (locationDataList) => {
  return uniqBy(locationDataList.filter(locationData => !!locationData.city), 'city')
      .map(locationData => ({ name: locationData.city, value: locationData.city }))
      .sort((a, b) => a.name.localeCompare(b.name));
});

export const selectDisplayNames = createSelector(selectUserLanguage, (lang) => {
  return new Intl.DisplayNames([lang], { type: 'region' });
});

export const selectCountries = (route: MainRoutes) => createSelector(selectFilteredLocationDataList(route),
    selectDisplayNames, (locationDataList, displayNames) => {
      return uniqBy(locationDataList.filter(locationData => !!locationData.country), 'country')
          .map(locationData => {
            let isValidCountryCode = isValidCountryISOCode(displayNames, locationData.country);

            const option: DropdownOption = {
              name: isValidCountryCode ? displayNames.of(locationData.country) : locationData.country,
              value: locationData.country,
            };

            return option;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
    });

export const selectStoreCodes = (route: MainRoutes) => createSelector(selectFilteredLocationDataList(route), (locationDataList) => {
  return uniqBy(locationDataList.filter(locationData => !!locationData.storeCode), 'storeCode')
      .map(locationData => ({ name: locationData.storeCode, value: locationData.storeCode }))
      .sort((a, b) => a.name.localeCompare(b.name));
});

export const selectBusinessNames = (route: MainRoutes) => createSelector(selectFilteredLocationDataList(route), (locationDataList) => {
  return uniqBy(locationDataList.filter(locationData => !!locationData.businessName), 'businessName')
      .map(locationData => ({ name: locationData.businessName, value: locationData.businessName }))
      .sort((a, b) => a.name.localeCompare(b.name));
});